import { createLogger } from '@utils/Logger'
import { DataStore, EMkStorageKeys } from '@utils/DataStore'
import { TMkAuthenticationAction, EMkAuthenticationAction, TMkAuthenticationState, TMkSession } from '../types'

// Create logger for the reducer
const log = createLogger('AuthenticationReducer')

/**
 * Generate a fresh initial authentication state.
 * This function is self-contained and does not import from services to avoid circular dependencies.
 * @returns A complete TMkAuthenticationState with all fields reset to initial values
 */
const getInitialState = (): TMkAuthenticationState => {
  const initialState: TMkAuthenticationState = {
    error: undefined,
    isAuthenticated: false,
    isAuthorized: false,
    isAuthInitialized: false,
    isLoggingOut: false,
    loginOrigin: null,
    session: undefined,
    failedLoginAttempts: 0,
    failedMfaAttempts: 0,
    isAccountLocked: false,
    isMfaLocked: false,
    lockoutEndTime: null,
    mfaLockoutEndTime: null,
    lockedUsername: null,
  }

  return initialState
}

/**
 * Reducer function to handle authentication state changes based on dispatched actions.
 * Reducer function managing authentication and authorization state transitions.
 *
 * @param {TMkAuthenticationState} state - The current state of authentication.
 * @param {TMkAuthenticationAction} action - The action dispatched to change the state.
 * @returns {TMkAuthenticationState} The new state after applying the action.
 *
 *
 * @remarks
 * Handles the following state transitions:
 * - Authentication flow (request, success, failure)
 * - Authorization flow (request, success, failure)
 * - Login flow (failure)
 * - Logout (with optional error)
 * - Registration flow (request, success, failure)
 * - Token refresh
 * - Authentication origin management
 * - Authorized user management
 *
 * Each action maintains or updates the following state properties:
 * - isAuthenticated: User authentication status
 * - isAuthorized: User authorization status
 * - user: Current user data
 * - error: Error information
 * - loginOrigin: Original login redirect path
 *
 * @example
 * // Example usage:
 * const newState = authenticationReducer(currentState, { type: AuthenticationActionType.LOGIN_SUCCESS, payload: user });
 */
export const authenticationReducer = (state: TMkAuthenticationState, action: TMkAuthenticationAction): TMkAuthenticationState => {
  let newState: TMkAuthenticationState

  switch (action.type) {
    case EMkAuthenticationAction.AUTHENTICATION_SUCCESS:
      newState = {
        ...state,
        isAuthenticated: true,
        isAuthorized: false,
        session: action.payload as TMkSession | undefined,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.AUTHORIZATION_SUCCESS:
      newState = {
        ...state,
        isAuthenticated: true,
        isAuthorized: true,
        session: action.payload,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.AUTHENTICATION_FAILURE:
    case EMkAuthenticationAction.AUTHORIZATION_FAILURE: {
      // Extract error messages for comparison
      const currentErrorMsg = state.error instanceof Error ? state.error.message : state.error?.message || state.error || ''
      const newErrorMsg = action.payload instanceof Error ? action.payload.message : action.payload?.message || action.payload || ''

      // Create a plain object error that will serialize properly in React state
      const errorObject = action.payload instanceof Error ? { message: action.payload.message, name: action.payload.name, stack: action.payload.stack } : action.payload

      newState = {
        ...state,
        isAuthenticated: false,
        isAuthorized: false,
        session: undefined,
        error: errorObject,
      }

      // Only log if this is a new error to prevent duplicate logging
      if (currentErrorMsg !== newErrorMsg) {
        log.error('🔴 [AuthReducer] AUTHENTICATION_FAILURE received', {
          actionType: action.type,
          error: newErrorMsg,
          errorObject,
          previousError: currentErrorMsg,
          isNewError: currentErrorMsg !== newErrorMsg,
        })
      } else {
        log.debug('🔄 [AuthReducer] Duplicate error ignored', {
          actionType: action.type,
          error: newErrorMsg,
          isDuplicate: true,
        })
      }
      break
    }

    case EMkAuthenticationAction.AUTHENTICATION_REQUEST:
      newState = {
        ...state,
        isAuthenticated: false,
        isAuthorized: false,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.AUTHORIZATION_REQUEST:
      newState = {
        ...state,
        isAuthenticated: true,
        isAuthorized: false,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.CLEAR_ERROR:
      newState = {
        ...state,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.START_LOGOUT:
      newState = {
        ...state,
        isLoggingOut: true,
      }
      break

    case EMkAuthenticationAction.LOGOUT:
      newState = {
        ...state,
        isAuthenticated: false,
        isAuthorized: false,
        isLoggingOut: false,
        session: undefined,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.LOGOUT_WITH_ERROR:
      newState = {
        ...state,
        error: action.payload,
        isAuthenticated: false,
        isAuthorized: false,
        isLoggingOut: false,
        session: undefined,
      }
      log.error('Logout with error', {
        error: action.payload instanceof Error ? action.payload.message : action.payload,
      })
      break

    case EMkAuthenticationAction.REGISTER_REQUEST:
      newState = {
        ...state,
        isAuthenticated: false,
      }
      break

    case EMkAuthenticationAction.REGISTER_SUCCESS:
      newState = {
        ...state,
        isAuthenticated: true,
        // user: action.payload,
      }
      break

    case EMkAuthenticationAction.REGISTER_FAILURE:
      newState = {
        ...state,
        error: action.payload,
      }
      log.error('Registration failure', {
        error: action.payload instanceof Error ? action.payload.message : action.payload,
      })
      break

    // case EMkAuthenticationAction.REFRESH_AUTHENTICATION:
    //   newState = {
    //     ...state,
    //     error: undefined,
    //   }
    //   log.debug('Authentication refresh', { timestamp })
    //   break

    case EMkAuthenticationAction.REVOKE_AUTHENTICATION:
      newState = {
        ...state,
        isAuthenticated: false,
        isAuthorized: false,
        session: undefined,
        error: undefined,
      }
      break

    case EMkAuthenticationAction.SET_AUTHENTICATION_ORIGIN:
      newState = {
        ...state,
        loginOrigin: action.payload,
      }
      break

    case EMkAuthenticationAction.REMOVE_AUTHENTICATION_ORIGIN:
      newState = {
        ...state,
        loginOrigin: null,
      }
      break

    case EMkAuthenticationAction.INCREMENT_FAILED_ATTEMPTS:
      newState = {
        ...state,
        failedLoginAttempts: state.failedLoginAttempts + 1,
      }
      break

    case EMkAuthenticationAction.INCREMENT_FAILED_MFA_ATTEMPTS:
      newState = {
        ...state,
        failedMfaAttempts: state.failedMfaAttempts + 1,
      }
      break

    case EMkAuthenticationAction.RESET_FAILED_ATTEMPTS:
      newState = {
        ...state,
        failedLoginAttempts: 0,
      }
      break

    case EMkAuthenticationAction.RESET_FAILED_MFA_ATTEMPTS:
      newState = {
        ...state,
        failedMfaAttempts: 0,
      }
      break

    case EMkAuthenticationAction.LOCK_ACCOUNT: {
      // Check if we're already locked with the same username to prevent duplicate logs
      const isAlreadyLocked = state.isAccountLocked && state.lockedUsername === action.payload.username

      newState = {
        ...state,
        isAccountLocked: true,
        lockoutEndTime: action.payload.lockoutEndTime,
        lockedUsername: action.payload.username,
      }

      // Only log and persist if this is a new lockout
      if (!isAlreadyLocked) {
        // Persist lockout state to localStorage
        DataStore.setItem(
          EMkStorageKeys.MAKO_ACCOUNT_LOCKOUT,
          {
            isAccountLocked: true,
            lockoutEndTime: action.payload.lockoutEndTime,
            lockedUsername: action.payload.username,
          },
          { store: localStorage },
        )

        log.warn('Account locked', {
          username: action.payload.username,
          lockoutEndTime: new Date(action.payload.lockoutEndTime).toISOString(),
        })
      } else {
        log.debug('Lockout state already set, skipping duplicate log')
      }
      break
    }

    case EMkAuthenticationAction.LOCK_MFA_ACCOUNT: {
      // Check if we're already MFA locked for the same username
      const isAlreadyMfaLocked = state.isMfaLocked && state.lockedUsername === action.payload.username

      newState = {
        ...state,
        isMfaLocked: true,
        mfaLockoutEndTime: action.payload.lockoutEndTime,
        lockedUsername: action.payload.username,
      }

      // Only log if this is a new MFA lockout
      if (!isAlreadyMfaLocked) {
        log.warn('MFA Account locked', {
          username: action.payload.username,
          mfaLockoutEndTime: new Date(action.payload.lockoutEndTime).toISOString(),
        })
      } else {
        log.debug('MFA lockout state already set, skipping duplicate log')
      }
      break
    }

    case EMkAuthenticationAction.UNLOCK_ACCOUNT:
      newState = {
        ...state,
        isAccountLocked: false,
        lockoutEndTime: null,
        lockedUsername: null,
      }

      // Clear persisted lockout state from localStorage
      DataStore.removeItem(EMkStorageKeys.MAKO_ACCOUNT_LOCKOUT, { store: localStorage })
      break

    case EMkAuthenticationAction.UNLOCK_MFA_ACCOUNT:
      newState = {
        ...state,
        isMfaLocked: false,
        mfaLockoutEndTime: null,
        lockedUsername: null,
      }
      break

    case EMkAuthenticationAction.RESET_STATE:
      // Nuclear option: completely reset to initial state
      // This is called during logout to ensure no user data persists
      return getInitialState()

    default:
      log.warn('Unknown authentication action', {
        actionType: (action as any).type,
      })
      return state
  }

  return newState
}

export default authenticationReducer
